import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import FormalRVHeader from '../components/formalrv/FormalRVHeader'
import { LevelBadge, STAGE_STATUS, Tag } from '../components/formalrv/badges'
import { fmtDuration, fmtExact, fmtMode, fmtQubits, fmtRatio, fmtSci, isDefaultMode } from '../components/formalrv/format'
import { formalrvApi, type FRVChecked, type FRVEntry } from '../utils/api'

const SERVICE_LABEL: Record<string, string> = {
  max_parallel_gates: 'Parallel gate slots',
  factories: 'Magic-state factories',
  decoders: 'Decoders',
  classical_data_bandwidth: 'Classical bandwidth (bits/µs)',
  qubit_routing_bandwidth: 'Routing bandwidth (qubits/µs)',
  ancilla_bandwidth: 'Fresh-ancilla bandwidth (qubits/µs)',
}

const SHIPPED_ROLES = new Set(['VM verdict', 'Compile report', 'Machine'])

function Card({ title, children, className = '' }: { title?: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={`bg-white rounded-2xl border border-qcloud-border p-5 sm:p-6 ${className}`}>
      {title && <h2 className="text-lg font-bold text-qcloud-text mb-3">{title}</h2>}
      {children}
    </section>
  )
}

function Figure({ label, qubits, days, sub, accent }: { label: string; qubits: string; days: string; sub: string; accent: string }) {
  return (
    <div className="flex-1 min-w-[220px] rounded-xl border border-qcloud-border p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-qcloud-muted">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: accent }} />{label}
      </div>
      <div className="mt-2 text-3xl font-semibold text-qcloud-text">{qubits}<span className="text-base font-normal text-qcloud-muted"> qubits</span></div>
      <div className="text-2xl font-semibold text-qcloud-text">{days}</div>
      <div className="text-xs text-qcloud-muted mt-1">{sub}</div>
    </div>
  )
}

function Headline({ e }: { e: FRVEntry }) {
  const c = e.checked
  const w = e.replay?.witnesses.find(w => w.physical_error === e.claim?.physical_error) ?? e.replay?.witnesses[0]
  return (
    <Card>
      <div className="flex flex-wrap gap-3 items-stretch">
        {e.claim && (
          <Figure label="Paper's claim" accent="#eb6834"
            qubits={`${e.claim.physical_qubits_qualifier === 'under' ? '< ' : ''}${fmtQubits(e.claim.physical_qubits)}`}
            days={fmtDuration(e.claim.runtime_days)} sub={e.claim.headline} />
        )}
        {c && (
          <Figure label="Checked by FormalRV" accent="#2a78d6"
            qubits={fmtQubits(c.peak_qubits)} days={fmtDuration(c.runtime_days)}
            sub={`Peak-qubit and runtime upper bounds, ${c.runtime_basis}${c.machine_qubits ? ` · machine of ${c.machine_qubits.toLocaleString()} qubits` : ''}`} />
        )}
        {!c && w && (
          <Figure label="Paper's equations replayed" accent="#1baf7a"
            qubits={fmtQubits(w.physical_qubits)} days={fmtDuration(w.expected_days)}
            sub="Expected time of a conditional serial witness; not a compiled schedule" />
        )}
        {!c && !w && (
          <div className="flex-1 min-w-[220px] rounded-xl border border-dashed border-qcloud-border p-4 text-sm text-qcloud-muted flex items-center">
            No timed schedule has been checked yet. See the verification chain below for how far the audit goes.
          </div>
        )}
        {e.claim && c && (
          <div className="min-w-[160px] rounded-xl bg-slate-50 p-4 flex flex-col justify-center">
            <div className="text-xs font-semibold uppercase tracking-wider text-qcloud-muted">Checked / claimed</div>
            <div className="mt-1 text-sm text-qcloud-text tabular-nums">time <span className="text-xl font-semibold">{fmtRatio(c.runtime_days, e.claim.runtime_days)}</span></div>
            <div className="text-sm text-qcloud-text tabular-nums">qubits <span className="text-xl font-semibold">{fmtRatio(c.peak_qubits, e.claim.physical_qubits)}</span></div>
          </div>
        )}
      </div>
      {e.expected_total && (
        <div className="mt-4 rounded-xl border border-qcloud-border overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-qcloud-muted text-left">
                <th className="px-4 py-2">Expected time to factor</th>
                <th className="px-3 py-2">Time per run</th>
                <th className="px-3 py-2">Success per run</th>
                <th className="px-3 py-2">Shots</th>
                <th className="px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              <tr className="border-t border-slate-100">
                <td className="px-4 py-2 text-qcloud-muted">Paper</td>
                <td className="px-3 py-2">{fmtDuration(e.expected_total.paper.time_per_run_h / 24)}</td>
                <td className="px-3 py-2">{e.expected_total.paper.success}</td>
                <td className="px-3 py-2">{e.expected_total.paper.shots}</td>
                <td className="px-3 py-2 font-semibold">{fmtDuration(e.expected_total.paper.total_days)}</td>
              </tr>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-2 text-qcloud-muted">Checked schedule</td>
                <td className="px-3 py-2">{fmtDuration(e.expected_total.checked.time_per_run_days)}</td>
                <td className="px-3 py-2">{e.expected_total.checked.success.toPrecision(3)}</td>
                <td className="px-3 py-2">{e.expected_total.checked.shots.toPrecision(3)}</td>
                <td className="px-3 py-2 font-semibold">{fmtDuration(e.expected_total.checked.total_days)}</td>
              </tr>
            </tbody>
          </table>
          <p className="px-4 py-2 text-[11px] text-qcloud-muted border-t border-slate-100">{e.expected_total.note}</p>
        </div>
      )}
    </Card>
  )
}

function Chain({ e }: { e: FRVEntry }) {
  return (
    <Card title="Verification chain">
      <ol className="grid md:grid-cols-5 gap-2">
        {e.chain.map((s, i) => {
          const st = STAGE_STATUS[s.status]
          return (
            <li key={s.id} className="relative rounded-xl border border-qcloud-border p-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] text-qcloud-muted">{i + 1} · {s.ext}</span>
                <span className={`w-3 h-3 rounded-full border-2 ${st.dot}`} aria-hidden />
              </div>
              <div className="font-semibold text-qcloud-text mt-1">{s.label}</div>
              <div className={`text-xs font-semibold mt-0.5 ${st.text}`}>{st.icon} {st.label}</div>
              <p className="text-xs text-qcloud-muted mt-1.5 leading-snug">{s.note}</p>
            </li>
          )
        })}
      </ol>
      <p className="text-xs text-qcloud-muted mt-3">
        Lean proves what the program means; the compiler and the independent resource VM check what the schedule costs on the declared machine.
        Lean target: <span className="font-mono text-qcloud-text">{e.lean_target}</span>
      </p>
    </Card>
  )
}

function Trust({ e }: { e: FRVEntry }) {
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Taken on trust">
        {e.trusted.length === 0
          ? <p className="text-sm text-qcloud-muted">No resource verdict yet, so no machine inputs are relied on.</p>
          : (
            <ul className="space-y-1.5 text-sm text-qcloud-text">
              {e.trusted.map(t => <li key={t} className="flex gap-2"><span className="text-qcloud-muted">○</span><span>{t}</span></li>)}
            </ul>
          )}
      </Card>
      <Card title="Read the result with">
        <ul className="space-y-2 text-sm text-qcloud-text">
          {e.caveats.map(c => <li key={c} className="flex gap-2"><span className="text-qcloud-muted">—</span><span>{c}</span></li>)}
        </ul>
        {e.open && (
          <p className="text-sm text-qcloud-text mt-3 pt-3 border-t border-slate-100">
            <span className="font-semibold">Still open: </span>{e.open}
          </p>
        )}
      </Card>
    </div>
  )
}

function Meter({ label, used, cap }: { label: string; used: number; cap: number | null }) {
  const pct = cap ? Math.min(100, (used / cap) * 100) : null
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-qcloud-text">{label}</span>
        <span className="tabular-nums text-qcloud-muted">
          <span className="text-qcloud-text font-semibold">{used.toLocaleString()}</span>
          {cap !== null && <> / {cap.toLocaleString()} ({pct!.toFixed(pct! < 1 ? 2 : 0)}%)</>}
        </span>
      </div>
      <div className="h-2 rounded-full bg-[#cde2fb] mt-1 overflow-hidden" aria-hidden>
        <div className="h-full rounded-full bg-[#2a78d6]" style={{ width: `${pct ?? 0}%` }} />
      </div>
    </div>
  )
}

function Verdict({ c }: { c: FRVChecked }) {
  const services = Object.entries(c.services)
  const machine = Object.entries(c.machine)
  const ops = Object.entries(c.opcode_counts).filter(([, v]) => v !== 0 && v !== '0')
  return (
    <Card title="Resource VM verdict">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
          ✓ {c.status}
        </span>
        <Tag>{fmtMode(c.vm_mode, c.require_specs)} mode</Tag>
        {!isDefaultMode(c.vm_mode, c.require_specs) && <Tag>not the default --require-specs mode</Tag>}
        {c.verdict_archived === false && <Tag>verdict recorded in the audit guide</Tag>}
        {c.cpu_seconds != null && <Tag>{Math.round(c.cpu_seconds).toLocaleString()} CPU-s</Tag>}
      </div>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div><dt className="text-xs text-qcloud-muted">Peak qubits (upper bound)</dt><dd className="font-mono text-qcloud-text">{fmtExact(c.peak_qubits)}</dd></div>
        <div><dt className="text-xs text-qcloud-muted">Runtime (upper bound, ns)</dt><dd className="font-mono text-qcloud-text break-all">{fmtExact(c.runtime_ns)}</dd></div>
      </dl>
      {c.scope && <p className="text-xs text-qcloud-muted mt-3 leading-relaxed"><span className="font-semibold text-qcloud-text">Scope of the check: </span>{c.scope}</p>}

      {services.length > 0 && (
        <div className="mt-5">
          <h3 className="text-sm font-semibold text-qcloud-text mb-2">Services reserved against machine capacity</h3>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
            {services.map(([k, v]) => (
              <Meter key={k} label={SERVICE_LABEL[k] ?? k} used={Number(v.used)} cap={v.capacity === null ? null : Number(v.capacity)} />
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        {machine.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-qcloud-text mb-2">Declared machine</h3>
            <table className="w-full text-xs">
              <tbody>
                {machine.map(([k, v]) => (
                  <tr key={k} className="border-t border-slate-100">
                    <td className="py-1 font-mono text-qcloud-muted">{k}</td>
                    <td className="py-1 text-right font-mono text-qcloud-text tabular-nums">{fmtExact(v.value)} <span className="text-qcloud-muted">{v.unit}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {ops.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-qcloud-text mb-2">Operation counts walked by the VM</h3>
            <table className="w-full text-xs">
              <tbody>
                {ops.map(([k, v]) => (
                  <tr key={k} className="border-t border-slate-100">
                    <td className="py-1 font-mono text-qcloud-muted">{k}</td>
                    <td className="py-1 text-right font-mono text-qcloud-text tabular-nums">{fmtExact(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  )
}

function Replay({ e }: { e: FRVEntry }) {
  if (!e.replay) return null
  return (
    <Card title="Replay of the paper's equations">
      <p className="text-sm text-qcloud-muted mb-3">{e.replay.evidence_kind}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-qcloud-muted text-left">
              <th className="px-3 py-2">Physical error</th><th className="px-3 py-2">Physical qubits</th>
              <th className="px-3 py-2">Expected time</th><th className="px-3 py-2">Expected shots</th><th className="px-3 py-2">Logical qubits</th>
              <th className="px-3 py-2">Prime-count rounding</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {e.replay.witnesses.map((w, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-3 py-2">{fmtSci(w.physical_error)}</td>
                <td className="px-3 py-2">{w.physical_qubits.toLocaleString()}</td>
                <td className="px-3 py-2">{fmtDuration(w.expected_days)}</td>
                <td className="px-3 py-2">{w.expected_shots?.toFixed(1) ?? '—'}</td>
                <td className="px-3 py-2">{w.logical_qubits?.toLocaleString() ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-qcloud-muted">
                  {w.prime_roundings.length > 1 ? `${w.prime_roundings.length} roundings give this witness` : w.prime_roundings[0] ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3 className="text-sm font-semibold text-qcloud-text mt-4 mb-1.5">Interpretations the replay makes</h3>
      <ul className="space-y-1 text-sm text-qcloud-text list-disc pl-5">
        {e.replay.assumptions.map(a => <li key={a}>{a}</li>)}
      </ul>
    </Card>
  )
}

function Parameters({ e }: { e: FRVEntry }) {
  const cl = e.claim
  const rows: [string, string][] = []
  if (cl?.physical_error) rows.push(['Physical error rate', fmtSci(cl.physical_error)])
  if (cl?.code_distance) rows.push(['Code distance', String(cl.code_distance)])
  if (cl?.cycle_us) rows.push(['Surface/QEC cycle', cl.cycle_us >= 1000 ? `${cl.cycle_us / 1000} ms` : `${cl.cycle_us} µs`])
  if (cl?.reaction_us) rows.push(['Reaction time', `${cl.reaction_us} µs`])
  if (cl?.toffolis) rows.push(['Toffolis (paper)', `${fmtSci(cl.toffolis, 4)}${cl.toffolis_basis ? ` — ${cl.toffolis_basis}` : ''}`])
  if (e.toffolis_checked) rows.push(['Toffolis (FormalRV)', `${fmtSci(e.toffolis_checked.value, 4)} — ${e.toffolis_checked.basis}`])
  if (rows.length === 0) return null
  return (
    <Card title="Paper parameters">
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k}><dt className="text-xs text-qcloud-muted">{k}</dt><dd className="text-qcloud-text">{v}</dd></div>
        ))}
      </dl>
      {cl && <p className="text-[11px] text-qcloud-muted mt-3">Claims quoted from <span className="font-mono">{cl.source}</span></p>}
    </Card>
  )
}

function Evidence({ e }: { e: FRVEntry }) {
  return (
    <Card title="Evidence and provenance">
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[640px]">
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-qcloud-muted text-left">
              <th className="px-3 py-2">Role</th><th className="px-3 py-2">File (FormalRV)</th><th className="px-3 py-2">md5</th><th className="px-3 py-2">Last changed</th>
            </tr>
          </thead>
          <tbody>
            {e.sources.map(s => (
              <tr key={s.role + s.path} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 text-qcloud-text whitespace-nowrap">{s.role}</td>
                <td className="px-3 py-2 font-mono text-qcloud-text break-all">
                  {s.path}{s.section && <div className="text-qcloud-muted font-sans">§ {s.section}</div>}
                  {SHIPPED_ROLES.has(s.role) && e.evidence_kind === 'vm_json' && (
                    <a href={formalrvApi.fileUrl(s.path)} className="ml-2 font-sans text-qcloud-primary hover:underline" download>download</a>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-qcloud-muted">{s.md5.slice(0, 12)}</td>
                <td className="px-3 py-2 text-qcloud-muted whitespace-nowrap" title={s.subject}><span className="font-mono">{s.commit.slice(0, 8)}</span> · {s.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-qcloud-muted mt-3">
        Snapshot of FormalRV commit <span className="font-mono">{e.formalrv.commit.slice(0, 10)}</span>, generated {new Date(e.generated_at).toLocaleString()}. {e.disclaimer}
      </p>
    </Card>
  )
}

export default function ShorEntryPage() {
  const { entryId } = useParams<{ entryId: string }>()
  const [e, setE] = useState<FRVEntry | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!entryId) return
    setE(null); setError(null)
    formalrvApi.getEntry(entryId).then(x => { setE(x); document.title = `${x.short_name} · FT Shor Leaderboard` })
      .catch(err => setError(err?.message || 'Failed to load'))
  }, [entryId])

  return (
    <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg">
      <FormalRVHeader crumb={e?.short_name} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <Link to="/shor" className="text-sm text-qcloud-muted hover:text-qcloud-primary">← All results</Link>
        {error && <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-800 text-sm">{error}</div>}
        {!e && !error && <div className="bg-white rounded-2xl border border-qcloud-border p-12 text-center text-qcloud-muted">Loading…</div>}
        {e && (
          <>
            <section>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <LevelBadge level={e.level} />
                {e.kind === 'baseline' && <Tag>reference, not ranked</Tag>}
                <span className="text-xs text-qcloud-muted">{e.workload} · {e.platform}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-qcloud-text leading-tight">{e.short_name}</h1>
              <p className="text-base text-qcloud-text mt-2">
                {e.url ? <a href={e.url} target="_blank" rel="noreferrer" className="hover:text-qcloud-primary">{e.title} ↗</a> : e.title}
              </p>
              <p className="text-sm text-qcloud-muted">{e.authors} · {e.year}</p>
              <p className="text-sm text-qcloud-muted mt-2 max-w-3xl"><span className="font-semibold text-qcloud-text">Idea tested: </span>{e.idea}</p>
            </section>

            <Headline e={e} />
            <Chain e={e} />
            <Trust e={e} />
            {e.checked && e.checked.machine && <Verdict c={e.checked} />}
            <Replay e={e} />
            <Parameters e={e} />
            <Evidence e={e} />
          </>
        )}
      </main>
    </div>
  )
}
